import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { m, AnimatePresence } from 'framer-motion';
import { Layout } from '../components/layout';
import {
  usePosts,
  useTags,
  useTagNameLookup,
  buildSizes,
} from '@lightive/shared';
import { EASING } from '@lightive/ui';

interface PortfolioGridPageProps {
  contentType: 'work' | 'lab';
  aspectRatio?: string;
  showHoverOverlay?: boolean;
  showDescription?: boolean;
  emptyLabel: string;
}

export default function PortfolioGridPage({
  contentType,
  aspectRatio = 'aspect-[4/3]',
  showHoverOverlay = false,
  showDescription = true,
  emptyLabel,
}: PortfolioGridPageProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const { posts, loading: postsLoading } = usePosts({
    type: contentType,
    status: 'published',
  });
  const { tags, loading: tagsLoading } = useTags();
  const { getTagNames } = useTagNameLookup(tags);

  // Get tags that are actually used by this content type's posts
  const usedTags = useMemo(() => {
    const ids = new Set(posts.flatMap((p) => p.tags));
    return tags.filter((t) => ids.has(t.id));
  }, [posts, tags]);

  // Filter posts by selected tags
  const filteredPosts = useMemo(() => {
    if (selectedTags.length === 0) return posts;
    return posts.filter((post) =>
      selectedTags.some((tagId) => post.tags.includes(tagId))
    );
  }, [selectedTags, posts]);

  const handleTagClick = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const clearFilters = () => setSelectedTags([]);

  const loading = postsLoading || tagsLoading;
  const linkPrefix = `/${contentType}`;

  return (
    <Layout>
      {/* Manifesto strip */}
      <section className="px-6 md:px-10 py-6 md:py-8 border-b border-[color:var(--color-rule)]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-10">
          <p className="font-en font-medium uppercase text-sm md:text-base leading-snug text-white max-w-xl">
            LIGHTIVE IS A SPATIAL MEDIA CREATIVE STUDIO BASED IN BUSAN.
            <br />
            WE TURN LIGHT INTO SPACE, AND SPACE INTO EXPERIENCE.
          </p>
          <p className="hidden md:block font-kr text-white/60 text-sm md:text-base md:text-right">
            빛은 공간을 바꾸고, 공간은 사람을 바꿉니다.
          </p>
        </div>
      </section>

      {/* Index body: sticky tag rail (lg+) / content column */}
      <section className="px-6 md:px-10 py-8 md:py-12 lg:grid lg:grid-cols-[180px_minmax(0,1fr)] lg:gap-8 lg:items-start">
        {/* Tag rail - desktop only */}
        <aside className="hidden lg:block sticky top-20 self-start">
          <p className="font-en text-xs uppercase tracking-widest text-white/40 mb-4">
            TAG
          </p>
          <nav
            className="flex flex-col gap-2.5"
            aria-label="Filter by tag"
          >
            <button
              type="button"
              onClick={clearFilters}
              className={`w-fit cursor-pointer text-left font-en text-sm uppercase transition-colors ${
                selectedTags.length === 0
                  ? 'text-white underline decoration-1 underline-offset-4'
                  : 'text-[color:var(--color-ink-3)] hover:text-white'
              }`}
            >
              ALL
            </button>
            {usedTags.map((tag) => {
              const isSelected = selectedTags.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleTagClick(tag.id)}
                  aria-pressed={isSelected}
                  className={`w-fit cursor-pointer text-left font-en text-sm uppercase transition-colors ${
                    isSelected
                      ? 'text-white underline decoration-1 underline-offset-4'
                      : 'text-[color:var(--color-ink-3)] hover:text-white'
                  }`}
                >
                  {tag.name}
                </button>
              );
            })}
          </nav>

          {/* Counter chips - only when a filter is active */}
          {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {selectedTags.map((tagId, i) => {
                const tag = usedTags.find((t) => t.id === tagId);
                return (
                  <span
                    key={tagId}
                    aria-label={tag ? `Active filter: ${tag.name}` : undefined}
                    className="tabular-nums inline-flex items-center px-2 py-1 border border-[color:var(--color-rule)] font-en text-[11px] text-white/60"
                  >
                    {String(i + 1).padStart(6, '0')}
                  </span>
                );
              })}
              <button
                type="button"
                onClick={clearFilters}
                className="cursor-pointer inline-flex items-center px-2 py-1 border border-[color:var(--color-rule)] font-en text-[11px] uppercase text-white/60 transition-colors hover:text-white"
              >
                RESET
              </button>
            </div>
          )}
        </aside>

        <div className="min-w-0">
          {/* Mobile chip row - sticky */}
          <div className="lg:hidden sticky top-16 md:top-20 z-30 -mx-6 md:-mx-10 mb-8 border-b border-white/5 bg-black/95 px-6 py-4 backdrop-blur-sm md:px-10">
            <m.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.3 }}
              className="flex items-center gap-3 overflow-x-auto scrollbar-hide"
            >
              <button
                type="button"
                onClick={clearFilters}
                className={`tag-chip flex-shrink-0 min-h-[44px] ${
                  selectedTags.length === 0 ? 'active' : ''
                }`}
              >
                All
              </button>

              {usedTags.map((tag) => {
                const isSelected = selectedTags.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagClick(tag.id)}
                    aria-pressed={isSelected}
                    className={`tag-chip flex-shrink-0 min-h-[44px] ${
                      isSelected ? 'active' : ''
                    }`}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </m.div>
          </div>

          {/* Portfolio Grid */}
          {loading ? (
            <div className="flex justify-center py-24">
              <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          ) : (
            <m.div
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 md:gap-x-8 md:gap-y-14"
            >
              <AnimatePresence mode="popLayout">
                {filteredPosts.map((post, index) => {
                  const isHovered = hoveredId === post.id;
                  const isSibling = hoveredId !== null && hoveredId !== post.id;
                  const tagNames = getTagNames(post.tags);

                  return (
                    <m.div
                      key={post.id}
                      layout
                      initial={{ opacity: 0, y: 30 }}
                      animate={{
                        opacity: isSibling ? 0.3 : 1,
                        y: 0,
                      }}
                      exit={{ opacity: 0 }}
                      transition={{
                        opacity: { duration: 0.25 },
                        layout: { duration: 0.3 },
                        y: { duration: 0.4, delay: index * 0.05 },
                      }}
                      onMouseEnter={() => setHoveredId(post.id)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <Link
                        to={`${linkPrefix}/${post.slug}`}
                        className="group block"
                      >
                        {/* Thumbnail */}
                        <div className={`relative ${aspectRatio} overflow-hidden bg-white/5 border-2 border-white/15`}>
                          <m.img
                            src={post.thumbnail}
                            srcSet={post.thumbnailSrcSet || undefined}
                            sizes={post.thumbnailSrcSet ? buildSizes('card') : undefined}
                            alt={post.title}
                            animate={{ scale: isHovered ? 1.05 : 1 }}
                            transition={{ duration: 0.3, ease: EASING.smooth }}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading={index < 6 ? 'eager' : 'lazy'}
                            decoding="async"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />

                          {/* Hover Overlay (lab mode only) */}
                          {showHoverOverlay && (
                            <m.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: isHovered ? 1 : 0 }}
                              transition={{ duration: 0.25 }}
                              className="absolute inset-0 bg-black/70 flex flex-col justify-end p-5"
                            >
                              <p className="font-kr text-sm text-white/80 line-clamp-3 leading-relaxed">
                                {post.description}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {tagNames.map((tagName) => (
                                  <span
                                    key={tagName}
                                    className="text-[10px] font-en font-bold uppercase tracking-widest text-white/50"
                                  >
                                    {tagName}
                                  </span>
                                ))}
                              </div>
                            </m.div>
                          )}
                        </div>

                        {/* Card meta - below image */}
                        <div className="mt-3">
                          {tagNames.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-1.5">
                              {tagNames.slice(0, 2).map((tagName) => (
                                <span
                                  key={tagName}
                                  className="font-en text-[11px] uppercase tracking-wider text-[color:var(--color-ink-3)]"
                                >
                                  {tagName}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="flex items-baseline justify-between gap-3">
                            <h3
                              className={`min-w-0 break-words font-en font-medium text-sm uppercase tracking-tight leading-tight text-white ${
                                contentType === 'lab' ? 'group-hover:text-highlight' : ''
                              }`}
                            >
                              {post.title.toUpperCase()}
                            </h3>
                            {post.date && (
                              <span className="tabular-nums flex-shrink-0 font-en text-sm text-white/40">
                                {post.date.getFullYear()}
                              </span>
                            )}
                          </div>

                          {showDescription && (
                            <p className="font-kr text-xs md:text-sm text-white/35 mt-2 line-clamp-2 leading-relaxed">
                              {post.description}
                            </p>
                          )}
                        </div>
                      </Link>
                    </m.div>
                  );
                })}
              </AnimatePresence>
            </m.div>
          )}

          {/* Empty State */}
          {!loading && filteredPosts.length === 0 && (
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-24 text-center"
            >
              <div
                className="edge-line is-animated"
                style={{ width: 'min(320px, 60vw)' }}
              />
              <p className="font-kr text-white/60 text-base md:text-lg mt-6">
                {selectedTags.length > 0
                  ? `선택한 태그에 해당하는 ${emptyLabel}이 없습니다.`
                  : `아직 등록된 ${emptyLabel}이 없습니다.`}
              </p>
              <p className="font-en uppercase text-xs tracking-widest text-white/40 mt-2">
                NEW WORK IN PROGRESS
              </p>
              {selectedTags.length > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="cursor-pointer font-en font-bold text-[11px] uppercase tracking-widest text-white/50 hover:text-white transition-colors mt-6"
                >
                  RESET FILTERS
                </button>
              )}
            </m.div>
          )}
        </div>
      </section>
    </Layout>
  );
}
